import jwt_decode from 'jwt-decode'
import { axios } from '@argon/app/axios'

const NAMESPACE = 'auth'

const state = {
  token: localStorage.getItem('token') || null,
  iat: 0,
  exp: 0,
  status: '',
  authenticated: false,
  isClientConfigured: false
}

const getters = {
  hasToken: (state) => !!state.token,
  hasValidToken: (state) => !!((state.exp > new Date() / 1000) | 0),
  status: (state) => state.status,
  authenticated: (state) => state.authenticated
}

const mutations = {
  SET_STATUS(state, status) {
    state.status = status
  },
  SET_AUTHENTICATED(state, auth) {
    state.authenticated = auth
  },
  SET_TOKEN(state, token) {
    state.token = token
  },
  SET_IAT(state, iat) {
    state.iat = iat
  },
  SET_EXP(state, exp) {
    state.exp = exp
  },
  SET_CONFIGURED(state, configured) {
    state.isClientConfigured = configured
  }
}

const actions = {
  setToken: ({ commit, dispatch }, token) => {
    return new Promise((resolve, reject) => {
      if (token) {
        let decoded = jwt_decode(token)
        const { iat, exp } = decoded
        localStorage.setItem('token', token)
        axios.defaults.headers.common['Authorization'] = `JWT ${token}`
        commit('SET_TOKEN', token)
        commit('SET_IAT', iat)
        commit('SET_EXP', exp)
        commit('SET_AUTHENTICATED', true)
        dispatch('realtime/connectToRemote', {}, { root: true }).catch(() => {})
      } else {
        localStorage.removeItem('token')
        delete axios.defaults.headers.common['Authorization']
        commit('SET_TOKEN', null)
        commit('SET_IAT', 0)
        commit('SET_EXP', 0)
        commit('SET_AUTHENTICATED', false)
        dispatch('realtime/disconnectFromRemote', {}, { root: true })
      }
      resolve(token)
    })
  },
  register: ({ commit, dispatch }, payload) => {
    return new Promise((resolve, reject) => {
      axios({
        url: '/register',
        data: payload,
        method: 'POST'
      })
        .then((resp) => {
          const { token } = resp.data
          dispatch('setToken', token).then((token) => {
            commit('SET_STATUS', 'success')
          })
          resolve(resp.data)
        })
        .catch((error) => {
          dispatch('setToken', false)
          reject(error)
        })
    })
  },
  verify: ({ commit, dispatch }, payload) => {
    return new Promise((resolve, reject) => {
      axios({
        url: '/verify',
        data: payload,
        method: 'POST'
      })
        .then(resolve)
        .catch(reject)
    })
  },
  signin: ({ commit, dispatch }, signinData) => {
    return new Promise((resolve, reject) => {
      commit('SET_STATUS', 'fetching')
      axios({
        url: '/signin',
        data: signinData,
        method: 'POST'
      })
        .then((resp) => {
          const { token } = resp.data
          dispatch('setToken', token).then(() => {
            dispatch('me/getProfile', {}, { root: true })
            commit('SET_CONFIGURED', true)
            resolve(resp.data)
          })
        })
        .catch(reject)
    })
  },
  refreshToken: ({ dispatch, state }) => {
    return new Promise((resolve, reject) => {
      axios({
        url: '/renew-token',
        data: { token: state.token },
        method: 'POST'
      })
        .then(({ data }) => {
          const { token } = data
          dispatch('setToken', token).then(resolve)
        })
        .catch((error) => {
          if (error.status_code === 304) {
            resolve(true)
          } else {
            dispatch('setToken', false).then(reject)
          }
        })
    })
  },
  changePassword({ commit, dispatch }, payload) {
    return new Promise((resolve, reject) => {
      axios({
        url: '/password/change',
        data: payload,
        method: 'POST'
      })
        .then(resolve)
        .catch((error) => {
          const { status, errors } = error
          let newErrors = errors.map((item) => (item.name === 'token' ? { ...item, name: 'resource' } : item))
          let modified = { status, errors: newErrors }
          reject(modified)
        })
    })
  },
  configureClient: ({ commit, state, dispatch, getters, rootGetters }) => {
    if (state.isClientConfigured) {
      return
    }
    if (state.token) {
      // set the token we found
      dispatch('setToken', state.token).then(() => {
        if (getters.hasValidToken) {
          return dispatch('me/loadProfile', {}, { root: true }).then(() => {
            if (rootGetters['app/isMultiOrg']) {
              dispatch('organizations/loadActiveOrganization', {}, { root: true })
            }
            if (rootGetters['organizations/defaultOrgId']) {
              dispatch('organizations/setDefaultOrganization', {}, { root: true })
            }
            commit('SET_CONFIGURED', true)
          })
        } else {
          return dispatch('signout')
        }
      })
    }
  },
  signout: ({ commit, dispatch }) => {
    return new Promise((resolve, reject) => {
      dispatch('setToken', false).then(() => {
        dispatch('me/clearProfile', {}, { root: true })
        dispatch('organizations/clearActiveOrganization', {}, { root: true })
        dispatch('organizations/clearOrganizations', {}, { root: true })
        commit('SET_CONFIGURED', false)
        resolve(true)
      })
    })
  },
  requestReset({ commit, dispatch }, payload) {
    return new Promise((resolve, reject) => {
      axios({
        url: '/password/request-change',
        data: payload,
        method: 'POST'
      })
        .then(({ data }) => {
          resolve(data)
        })
        .catch(reject)
    })
  },
  getInvitation({}, refid) {
    return new Promise((resolve, reject) => {
      axios({
        url: `/invitation-detail/${refid}`,
        method: 'GET'
      })
        .then(({ data }) => {
          resolve(data)
        })
        .catch(reject)
    })
  }
}

export default {
  namespaced: true,
  state: state,
  getters: getters,
  mutations: mutations,
  actions: actions
}

export { NAMESPACE }
